import { supabase } from "../lib/supabase";
import { KOFI_CONFIG } from "../config/kofi";
import { checkSupporterStatus } from "./supporterService";

const API_BASE =
  (process.env.REACT_APP_AI_API_URL || process.env.REACT_APP_AI_API_BASE_URL || "https://colobongo.onrender.com").replace(/\/$/, "");
const DEVICE_ID_KEY = "colobongo_scan_device_id";
// Bumped from v1 -> v2 alongside the timezone fix in `_getTodayKey`.
// Bumped from v2 -> v3 on 2026-07-17 : restitution serveur des colopoints
// perdus pendant l'incident du Mur (09/07 -> 16/07). Le compteur local se
// fusionne en Math.max avec le serveur — sans bump, les appareils
// continueraient d'afficher les points comme dépensés jusqu'au lendemain
// malgré la remise à zéro côté serveur. Le backend reste la source de
// vérité ; on ne perd que le compteur anti-fraude local du jour.
const LEGACY_LOCAL_SCAN_USAGE_KEYS = [
  "colobongo_scan_usage_v1",
  "colobongo_scan_usage_v2",
];
const LOCAL_SCAN_USAGE_KEY = "colobongo_scan_usage_v3";
const DEFAULT_DAILY_LIMIT = KOFI_CONFIG.scanLimits.free;
const QUOTA_FETCH_TIMEOUT_MS = 5000;

try {
  if (typeof window !== "undefined" && window.localStorage) {
    LEGACY_LOCAL_SCAN_USAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  }
} catch (_error) {
  // best-effort cleanup
}

// MUST match backend `SCAN_QUOTA_TIMEZONE` (defaults to America/Toronto in
// server.py). If the local cache and the backend disagree on which day
// "today" is, the Math.max merge in `_mergeWithLocalUsage` poisons the
// next morning's quota with the previous evening's count, and the user
// sees 0 scans remaining even though they have not used any "today".
const QUOTA_TIMEZONE =
  process.env.REACT_APP_SCAN_QUOTA_TIMEZONE || "America/Toronto";

export const SCAN_COSTS = Object.freeze({
  fridgeOnly: 0, // Saving to the Fridge is free; only the cap applies.
  aiAnalysis: 1, // Basic AI scan costs 1 (free users get 3/day, supporters 10/day).
  // Submitting to the Wall is FREE — the right to publish is included in the
  // Analyse IA cost (1 scan). No second charge when sharing afterwards.
  wallSubmission: 0,
});

function _getTodayKey() {
  // Format as YYYY-MM-DD in QUOTA_TIMEZONE so it matches the backend's
  // ai_scan_usage_daily.usage_date computation. Using formatToParts to be
  // robust to locale/format differences (some browsers return "M/D/YYYY"
  // for en-CA depending on platform).
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: QUOTA_TIMEZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date());
    const year = parts.find((p) => p.type === "year")?.value;
    const month = parts.find((p) => p.type === "month")?.value;
    const day = parts.find((p) => p.type === "day")?.value;
    if (year && month && day) {
      return `${year}-${month}-${day}`;
    }
  } catch (_error) {
    // Fall through to UTC fallback below.
  }
  return new Date().toISOString().slice(0, 10);
}

function _readLocalScanUsage() {
  try {
    const raw = localStorage.getItem(LOCAL_SCAN_USAGE_KEY);
    if (!raw) return { date: _getTodayKey(), used: 0 };
    const data = JSON.parse(raw);
    if (data?.date !== _getTodayKey()) return { date: _getTodayKey(), used: 0 };
    return { date: data.date, used: Math.max(0, Number(data.used) || 0) };
  } catch {
    return { date: _getTodayKey(), used: 0 };
  }
}

function _writeLocalScanUsage(used) {
  try {
    localStorage.setItem(LOCAL_SCAN_USAGE_KEY, JSON.stringify({ date: _getTodayKey(), used }));
  } catch {
    // noop
  }
}

function _mergeWithLocalUsage(quota) {
  const local = _readLocalScanUsage();
  const serverUsed = Number(quota.scansUsedToday || 0);
  const effectiveUsed = Math.max(serverUsed, local.used);
  const dailyLimit = Number(quota.dailyLimit || DEFAULT_DAILY_LIMIT);
  return {
    ...quota,
    scansUsedToday: effectiveUsed,
    scansRemaining: Math.max(0, dailyLimit - effectiveUsed),
    quotaExceeded: effectiveUsed >= dailyLimit,
    quotaOk: effectiveUsed < dailyLimit,
  };
}

function _incrementLocalUsage(units = 1) {
  const local = _readLocalScanUsage();
  local.used += units;
  _writeLocalScanUsage(local.used);
  return local.used;
}

// Public helper so resilient flows (e.g. fridge-only direct save) can keep the
// local quota counter in sync when the backend is unreachable.
export function incrementLocalScanUsage(units = 1) {
  const safeUnits = Math.max(1, Math.round(Number(units) || 1));
  return _incrementLocalUsage(safeUnits);
}

// Miroir de incrementLocalScanUsage pour les remboursements : sans ce
// décrément, la fusion Math.max avec le serveur garderait le point affiché
// comme dépensé sur cet appareil jusqu'au lendemain.
export function decrementLocalScanUsage(units = 1) {
  const safeUnits = Math.max(1, Math.round(Number(units) || 1));
  const local = _readLocalScanUsage();
  const next = Math.max(0, local.used - safeUnits);
  _writeLocalScanUsage(next);
  return next;
}

// Export minimal pour l'affichage optimiste dans useQuota (lecture sync de
// localStorage au tout premier rendu, avant le fetch reseau).
export function readLocalScanUsageSnapshot() {
  return _readLocalScanUsage();
}

function isOffline() {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

function getOrCreateScanDeviceId() {
  const existing = localStorage.getItem(DEVICE_ID_KEY);
  if (existing) return existing;

  const generated = `scan-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  localStorage.setItem(DEVICE_ID_KEY, generated);
  return generated;
}

async function getAccessToken() {
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token || null;
  } catch (_error) {
    return null;
  }
}

function normalizeQuotaPayload(payload) {
  return {
    quotaOk: Boolean(payload?.quota_ok),
    quotaExceeded: Boolean(payload?.quota_exceeded),
    scansRemaining: Number(payload?.scans_remaining ?? payload?.scansRemaining ?? 0),
    scansUsedToday: Number(payload?.scans_used_today ?? payload?.scansUsedToday ?? 0),
    dailyLimit: Number(payload?.daily_limit ?? payload?.dailyLimit ?? DEFAULT_DAILY_LIMIT),
    message: payload?.message || null,
    isSupporter: Boolean(payload?.is_supporter ?? payload?.isSupporter),
  };
}

function quotaExceededMessage(language = "en") {
  return language === "fr"
    ? "Tu as utilise tes scans disponibles pour aujourd'hui. Reviens plus tard ou passe en Contributeur Bongo."
    : "You have used your scans for today. Come back later or become a Bongo Contributor.";
}

function resolveQuotaWithSupporter(payload, supporterStatus) {
  const normalized = normalizeQuotaPayload(payload);
  const scansUsedToday = Number.isFinite(normalized.scansUsedToday) ? normalized.scansUsedToday : 0;
  const apiLimit = Number.isFinite(normalized.dailyLimit) && normalized.dailyLimit > 0 ? normalized.dailyLimit : 0;
  const isSupporter = Boolean(supporterStatus?.isSupporter || normalized.isSupporter);
  const supporterLimit = Number.isFinite(Number(supporterStatus?.scanLimit))
    ? Number(supporterStatus.scanLimit)
    : KOFI_CONFIG.scanLimits.contributor;
  const preferredLimit = isSupporter
    ? supporterLimit
    : DEFAULT_DAILY_LIMIT;
  const dailyLimit = apiLimit > 0 ? Math.max(preferredLimit, apiLimit) : preferredLimit;
  const scansRemaining = Math.max(0, dailyLimit - scansUsedToday);
  const quotaExceeded = scansUsedToday >= dailyLimit;

  return {
    quotaOk: !quotaExceeded,
    quotaExceeded,
    scansRemaining,
    scansUsedToday,
    dailyLimit,
    isSupporter,
  };
}

export async function fetchAIScanQuotaStatus() {
  const token = await getAccessToken();
  const supporterStatus = await checkSupporterStatus();
  const dailyLimit = supporterStatus?.isSupporter
    ? KOFI_CONFIG.scanLimits.contributor
    : DEFAULT_DAILY_LIMIT;

  if (isOffline()) {
    return {
      quotaOk: false,
      quotaExceeded: false,
      scansRemaining: 0,
      scansUsedToday: 0,
      dailyLimit,
      isSupporter: Boolean(supporterStatus?.isSupporter),
      offline: true,
      message: "offline",
    };
  }

  const headers = {
    "X-Scan-Device-Id": getOrCreateScanDeviceId(),
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), QUOTA_FETCH_TIMEOUT_MS);

  try {
    const response = await fetch(`${API_BASE}/api/ai/quota-status`, {
      method: "GET",
      headers,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error("Could not fetch AI scan quota status");
    }

    const payload = await response.json();
    const resolved = resolveQuotaWithSupporter(payload, supporterStatus);
    return _mergeWithLocalUsage(resolved);
  } catch (error) {
    clearTimeout(timeoutId);
    const isTimeout = error?.name === "AbortError";
    // eslint-disable-next-line no-console
    console.warn(
      "[aiScanService] quota fetch failed or timed out:",
      isTimeout ? "timeout" : error
    );

    // Fallback : estimation basee sur localStorage + statut supporter connu.
    // Le backend reste la source de verite ; ce fallback evite uniquement
    // le spinner infini en cas de cold start Render.
    const local = _readLocalScanUsage();
    const effectiveUsed = Math.max(0, Number(local.used) || 0);
    const remaining = Math.max(0, dailyLimit - effectiveUsed);
    return {
      quotaOk: remaining > 0,
      quotaExceeded: remaining <= 0,
      scansRemaining: remaining,
      scansUsedToday: effectiveUsed,
      dailyLimit,
      isSupporter: Boolean(supporterStatus?.isSupporter),
      offline: true,
      message: isTimeout ? "timeout" : "offline",
    };
  }
}

export async function ensureAIScanQuota({
  scanCost = 1,
  language = "en",
} = {}) {
  const normalizedCost = Math.max(1, Math.round(Number(scanCost) || 1));
  const quota = await fetchAIScanQuotaStatus();
  if (quota?.offline) {
    const payload = {
      message: language === "fr"
        ? "Tu sembles hors ligne. Reconnecte-toi pour vérifier ton quota."
        : "You seem to be offline. Reconnect to check your quota.",
      quota_exceeded: false,
      quota_ok: false,
      scans_remaining: 0,
      scans_used_today: Number(quota.scansUsedToday || 0),
      daily_limit: Number(quota.dailyLimit || DEFAULT_DAILY_LIMIT),
      is_supporter: Boolean(quota.isSupporter),
      offline: true,
    };
    throw createQuotaError(503, payload, quota);
  }

  const remaining = Number(quota.scansRemaining || 0);
  if (remaining < normalizedCost) {
    const payload = {
      message: quotaExceededMessage(language),
      quota_exceeded: true,
      quota_ok: false,
      scans_remaining: remaining,
      scans_used_today: Number(quota.scansUsedToday || 0),
      daily_limit: Number(quota.dailyLimit || DEFAULT_DAILY_LIMIT),
      is_supporter: Boolean(quota.isSupporter),
    };
    throw createQuotaError(429, payload, quota);
  }

  return quota;
}

/**
 * Rembourse 1 colopoint après un ÉCHEC d'envoi au Mur (l'Analyse IA payée
 * incluait le droit de publier — si la publication plante, on redonne le
 * point pour que la personne puisse réessayer).
 *
 * Best-effort : retourne le quota rafraîchi si le remboursement a eu lieu,
 * null sinon (pas connecté, soumission en fait réussie, backend injoignable).
 * Ne lève JAMAIS — appelé depuis un gestionnaire d'erreur.
 */
export async function refundAIScanQuotaAfterWallFailure() {
  try {
    const token = await getAccessToken();
    if (!token) return null;

    const response = await fetch(`${API_BASE}/api/ai/quota-refund-wall-failure`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Scan-Device-Id": getOrCreateScanDeviceId(),
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) return null;

    const payload = await response.json().catch(() => ({}));
    decrementLocalScanUsage(1);
    const supporterStatus = await checkSupporterStatus();
    const quota = resolveQuotaWithSupporter(payload, supporterStatus);
    return _mergeWithLocalUsage(quota);
  } catch (_error) {
    return null;
  }
}

export async function consumeAIScanQuota({
  scanCost = 1,
  language = "en",
} = {}) {
  const normalizedCost = Math.max(1, Math.round(Number(scanCost) || 1));
  const token = await getAccessToken();
  const headers = {
    "Content-Type": "application/json",
    "X-Scan-Device-Id": getOrCreateScanDeviceId(),
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}/api/ai/quota-consume`, {
    method: "POST",
    headers,
    body: JSON.stringify({ units: normalizedCost }),
  });

  const payload = await response.json().catch(() => ({}));
  const supporterStatus = await checkSupporterStatus();
  const quota = resolveQuotaWithSupporter(payload?.detail || payload, supporterStatus);

  if (!response.ok || quota.quotaExceeded) {
    const errorPayload = payload?.detail || payload || {};
    if (!errorPayload.message) {
      errorPayload.message = quotaExceededMessage(language);
    }
    throw createQuotaError(response.status || 429, errorPayload, quota);
  }

  _incrementLocalUsage(normalizedCost);
  const merged = _mergeWithLocalUsage(quota);
  return merged;
}

function createQuotaError(status, payload, quota) {
  const error = new Error(payload?.message || "AI analysis failed");
  error.status = status;
  error.quotaExceeded = Boolean(payload?.quota_exceeded) || status === 429 || Boolean(quota?.quotaExceeded);
  error.quota = quota || normalizeQuotaPayload(payload || {});
  return error;
}

export async function analyzeColoringWithAI({
  file,
  language = "en",
  skillLevel = "intermediate",
  brand = "",
  textureType = "",
  challengeCategory = "",
}) {
  if (!file) {
    throw new Error("No file provided for AI analysis");
  }

  if (isOffline()) {
    const offlineError = new Error(
      language === "fr"
        ? "Tu sembles hors ligne. Reconnecte-toi puis relance le scan."
        : "You seem to be offline. Reconnect and try the scan again."
    );
    offlineError.offline = true;
    throw offlineError;
  }

  const token = await getAccessToken();
  const headers = {
    "X-Scan-Device-Id": getOrCreateScanDeviceId(),
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const formData = new FormData();
  formData.append("image", file);
  formData.append("mode", "real");
  formData.append("lang", language === "fr" ? "fr" : "en");
  formData.append("skill", skillLevel || "intermediate");
  formData.append("brand", brand || "");
  if (textureType) {
    formData.append("texture_type", textureType);
  }
  if (challengeCategory) {
    formData.append("challenge_category", challengeCategory);
  }

  const response = await fetch(`${API_BASE}/api/ai/analyze`, {
    method: "POST",
    headers,
    body: formData,
  });

  const payload = await response.json().catch(() => ({}));
  const supporterStatus = await checkSupporterStatus();
  const quota = resolveQuotaWithSupporter(payload?.detail || payload, supporterStatus);

  if (!response.ok) {
    throw createQuotaError(response.status, payload?.detail || payload, quota);
  }

  const mergedQuota = _mergeWithLocalUsage(quota);

  return {
    ...payload,
    quota: mergedQuota,
  };
}

export async function saveColoringToFridgeWithoutAI({
  file,
  language = "en",
  title = "",
  challengeContext = null,
}) {
  if (!file) {
    throw new Error("No file provided for Fridge save");
  }

  if (isOffline()) {
    const offlineError = new Error(
      language === "fr"
        ? "Tu sembles hors ligne. Reconnecte-toi puis réessaie."
        : "You seem to be offline. Reconnect and try again."
    );
    offlineError.offline = true;
    throw offlineError;
  }

  const token = await getAccessToken();
  if (!token) {
    const authError = new Error(
      language === "fr"
        ? "Connexion necessaire pour enregistrer sur le Frigo."
        : "Sign-in is required to save to the Fridge."
    );
    authError.authRequired = true;
    throw authError;
  }

  const headers = {
    "X-Scan-Device-Id": getOrCreateScanDeviceId(),
    Authorization: `Bearer ${token}`,
  };

  const formData = new FormData();
  formData.append("image", file);
  formData.append("lang", language === "fr" ? "fr" : "en");
  formData.append("title", title || "");
  if (challengeContext) {
    formData.append("challenge_context", JSON.stringify(challengeContext));
  }

  const response = await fetch(`${API_BASE}/api/ai/fridge-save`, {
    method: "POST",
    headers,
    body: formData,
  });

  const payload = await response.json().catch(() => ({}));
  const supporterStatus = await checkSupporterStatus();
  const quota = resolveQuotaWithSupporter(payload?.detail || payload, supporterStatus);

  if (!response.ok) {
    throw createQuotaError(response.status, payload?.detail || payload, quota);
  }

  const mergedQuota = _mergeWithLocalUsage(quota);

  return {
    ...payload,
    quota: mergedQuota,
  };
}
